import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import {
  getMonitorRequestsForUserPath,
  getEbinderSectionPath,
} from "../services/firestorePaths";
import { useParams, useNavigate } from "react-router-dom";

// Display label -> Firestore collection key
const SECTION_MAP = {
  "1. Protocol & Investigator Brochure": "protocol_and_ib",
  "2. Site Information": "site_information",
  "3. Study Team Credentials": "study_team_credentials",
  "4. IRB Documents": "irb_documents",
  "5. Sponsor Documents": "sponsor_documents",
  "6. Regulatory Submissions": "regulatory_submissions",
  "7. Safety Reporting": "safety_reporting",
  "8. Correspondence": "correspondence",
  "9. Training Documents": "training_documents",
  "10. Study Logs": "study_logs",
  "11. Monitoring Visits": "monitoring_visits",
  "12. Close-Out Documents": "closeout",
};

export default function MonitorViewPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [documents, setDocuments] = useState({});
  const [expanded, setExpanded] = useState({});

  // Load Request (scoped to current monitor)
  useEffect(() => {
    const load = async () => {
      try {
        const user = auth.currentUser;
        if (!user || !requestId) return;

        const reqRef = doc(db, getMonitorRequestsForUserPath(user.uid), requestId);
        const snap = await getDoc(reqRef);
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setRequest(data);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [requestId]);

  // Load docs for approved/requested sections
  useEffect(() => {
    const fetchDocs = async () => {
      if (!request || String(request.status || "").toLowerCase() !== "approved") return;

      const bySection = {};
      const initialExpanded = {};

      const sections = request.requestedSections || [];
      for (const label of sections) {
        const key = SECTION_MAP[label];
        if (!key) continue;

        const snap = await getDocs(collection(db, getEbinderSectionPath(request.studyId, key)));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        bySection[label] = rows;

        // expand sections that have docs; keep others collapsed
        initialExpanded[label] = rows.length > 0;
      }

      setDocuments(bySection);
      setExpanded((prev) => ({ ...initialExpanded, ...prev })); // don’t collapse a user-open section
    };

    fetchDocs();
  }, [request]);

  // Status chip
  const statusChip = useMemo(() => {
    if (!request || !request.status) return null;
    const s = String(request.status);
    const isApproved = s.toLowerCase() === "approved";
    const color = isApproved
      ? "bg-green-100 text-green-800 ring-green-300"
      : "bg-amber-100 text-amber-800 ring-amber-300";
    const dot = isApproved ? "bg-green-500" : "bg-amber-500";
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${color}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        {isApproved ? "Approved" : s}
      </span>
    );
  }, [request]);

  // Small file “icon” based on extension
  const fileIcon = (name) => {
    const ext = (name || "").split(".").pop()?.toLowerCase();
    if (ext === "pdf") return "📄";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "🖼️";
    if (["doc", "docx"].includes(ext)) return "📝";
    if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
    if (["ppt", "pptx"].includes(ext)) return "📑";
    return "📎";
  };

  const toggle = (label) => setExpanded((p) => ({ ...p, [label]: !p[label] }));

  // UI
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="rounded-xl border bg-white p-8 shadow">
          <p className="text-red-600">Request not found.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 px-4 py-2 rounded bg-gray-700 text-white hover:bg-gray-800"
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  const isApproved = String(request.status || "").toLowerCase() === "approved";

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100"
      style={{
        backgroundImage: "url('/bg_ai.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundBlendMode: "soft-light",
      }}
    >
      <div className="mx-auto max-w-5xl px-4 py-8">
        {/* Header Card */}
        <div className="rounded-2xl bg-white/90 backdrop-blur shadow-lg ring-1 ring-black/5 p-6 md:p-8 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
                {request.studyName || "Study"}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Approved Sections visible below. Click a section to expand and open files.
              </p>
            </div>
            <div className="flex flex-col items-end gap-3">
              {statusChip}
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center rounded-lg bg-gray-800 px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-gray-900"
                aria-label="Go back"
              >
                ← Back
              </button>
            </div>
          </div>
        </div>

        {/* Gate for unapproved */}
        {!isApproved ? (
          <div className="rounded-xl bg-white/90 backdrop-blur p-8 shadow ring-1 ring-black/5">
            <p className="text-amber-700">
              This request is not approved yet. You’ll see documents here once the admin approves.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.keys(documents).length === 0 && (
              <div className="rounded-xl bg-white/90 backdrop-blur p-8 shadow ring-1 ring-black/5">
                <p className="text-gray-600">No documents uploaded yet.</p>
              </div>
            )}

            {/* Section Cards */}
            {Object.entries(documents).map(([label, docs]) => (
              <div
                key={label}
                className="rounded-2xl overflow-hidden bg-white/90 backdrop-blur shadow ring-1 ring-black/5"
              >
                <button
                  onClick={() => toggle(label)}
                  className="w-full flex items-center justify-between px-5 py-4 md:px-6 md:py-5 hover:bg-white/60"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-sm font-semibold">
                      {docs.length}
                    </span>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-900">
                      {label}
                    </h3>
                  </div>
                  <span
                    className={`transition-transform ${expanded[label] ? "rotate-180" : ""}`}
                    aria-hidden
                  >
                    ▾
                  </span>
                </button>

                {expanded[label] && (
                  <div className="px-5 pb-5 md:px-6 md:pb-6">
                    {docs.length === 0 ? (
                      <p className="text-sm italic text-gray-500">No documents uploaded yet.</p>
                    ) : (
                      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                        {docs.map((d) => (
                          <li key={d.id} className="flex items-center justify-between gap-4 px-4 py-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-base">{fileIcon(d.fileName)}</span>
                                <a
                                  href={d.fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="truncate text-indigo-700 hover:underline font-medium"
                                  title={d.fileName || d.title || "Open"}
                                >
                                  {d.fileName || d.title || "Open"}
                                </a>
                              </div>
                              {(d.version || d.uploadedAt) && (
                                <p className="mt-0.5 text-xs text-gray-500">
                                  {d.version ? `v${d.version}` : ""}
                                  {d.version && d.uploadedAt ? " • " : ""}
                                  {d.uploadedAt && d.uploadedAt.seconds
                                    ? new Date(d.uploadedAt.seconds * 1000).toLocaleString()
                                    : ""}
                                </p>
                              )}
                            </div>
                            <a
                              href={d.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="shrink-0 inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              Open
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
